/**
 * Notifications inbox — unread-tinted rows with category icons and dots,
 * tap-to-read, mark-all-read. Wired: GET /api/account/notifications,
 * POST /api/account/notifications/<id>/read, POST /api/account/notifications/read-all
 * (both optimistic, reverting on error). Port of screens-account.jsx NotificationsInbox.
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

import { space } from '@bero/tokens';
import { bool, fmtWhen, str, useGateList } from '@/components/gate/api';
import { goBack, IconCircle } from '@/features/account-admin/bits';
import { Icon, type IconName } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, EmptyState, Hairline, IconButton, Screen, Skeleton, TopBar } from '@/ui';

type Notif = { id: string; title: string; body: string; when: string; cat: string; read: boolean };

const CAT_ICON: Record<string, IconName> = {
  visitors: 'user-plus', security: 'shield', notices: 'megaphone', payments: 'credit-card',
  bookings: 'calendar', events: 'calendar', polls: 'list', community: 'message-square',
};
// notification category (API value, singular or plural) → localised label
const CAT_LABEL: Record<string, DictKey> = {
  visitor: 'nc_visitor', visitors: 'nc_visitor', security: 'nc_security',
  notice: 'nc_notice', notices: 'nc_notice', payment: 'nc_payment', payments: 'nc_payment',
  booking: 'nc_booking', bookings: 'nc_booking', event: 'nc_event', events: 'nc_event',
  poll: 'nc_poll', polls: 'nc_poll', community: 'nc_community', system: 'nc_system',
  helpdesk: 'nc_helpdesk', maintenance: 'nc_maintenance',
};

function toRow(n: unknown, i: number): Notif {
  return {
    id: str(n, 'id') ?? `n${i}`,
    title: str(n, 'title') ?? 'Notification',
    body: str(n, 'body', 'message', 'text') ?? '',
    when: fmtWhen(str(n, 'createdAt', 'at'), str(n, 'when')),
    cat: str(n, 'cat', 'category', 'kind') ?? 'General',
    read: bool(n, 'read', 'isRead', 'seen') ?? false,
  };
}

export default function NotificationsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, setItems, state } = useGateList<unknown>('/api/account/notifications', 'notifications');
  const rows = items.map(toRow);
  const [notice, setNotice] = React.useState<string | null>(null);

  const markRead = (id: string) => {
    const prev = items;
    const target = rows.find((r) => r.id === id);
    if (!target || target.read) return;
    setNotice(null);
    setItems(prev.map((n, i) => (toRow(n, i).id === id ? { ...(n as object), read: true, isRead: true } : n)));
    api.post<unknown>(`/api/account/notifications/${id}/read`).catch(() => {
      setItems(prev);
      setNotice(t('nb_read_error'));
    });
  };

  const markAllRead = () => {
    if (!rows.some((r) => !r.read)) return;
    const prev = items;
    setNotice(null);
    setItems(prev.map((n) => ({ ...(n as object), read: true, isRead: true })));
    api.post<unknown>('/api/account/notifications/read-all').catch(() => {
      setItems(prev);
      setNotice(t('nb_read_all_error'));
    });
  };

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('sec_notifications')}
        onBack={goBack}
        right={
          <IconButton label={t('nb_mark_all')} onPress={markAllRead}>
            <Icon name="check" size={20} color={colors.textPrimary} />
          </IconButton>
        }
      />
      <Screen>
        {notice ? (
          <View style={{ paddingHorizontal: space.s4, paddingTop: space.s3 }}>
            <AppText variant="bodySm" tone="danger">{notice}</AppText>
          </View>
        ) : null}
        {state === 'loading' ? (
          <View style={{ gap: space.s3, paddingHorizontal: space.s4, paddingTop: space.s4 }}>
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} h={72} />)}
          </View>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="bell" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? t('nb_err_title') : t('nb_empty_title')}
            body={state === 'error' ? t('nb_err_body') : t('nb_empty_body')}
          />
        ) : (
          <>
            {rows.map((n) => (
              <View key={n.id}>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => markRead(n.id)}
                  style={{
                    flexDirection: 'row', gap: space.s3, paddingVertical: 14, paddingHorizontal: space.s4,
                    backgroundColor: n.read ? 'transparent' : colors.primary50,
                  }}
                >
                  <IconCircle name={CAT_ICON[n.cat.toLowerCase()] ?? 'bell'} />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.s2 }}>
                      <AppText variant="bodyMd" weight="500" style={{ flex: 1 }}>{n.title}</AppText>
                      {!n.read ? (
                        <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.primary500 }} />
                      ) : null}
                    </View>
                    {n.body ? <AppText variant="bodySm" tone="secondary" style={{ marginTop: 2 }}>{n.body}</AppText> : null}
                    <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                      {[n.when, CAT_LABEL[n.cat.toLowerCase()] ? t(CAT_LABEL[n.cat.toLowerCase()]) : n.cat].filter(Boolean).join(' · ')}
                    </AppText>
                  </View>
                </Pressable>
                <Hairline />
              </View>
            ))}
            <View style={{ paddingHorizontal: space.s4, marginTop: space.s3 }}>
              <Button title={t('np_title')} kind="tertiary" onPress={() => router.push('/account/notification-prefs')} />
            </View>
          </>
        )}
      </Screen>
    </Screen>
  );
}
